import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { FormStatus } from "@bca-shared/models/_enums";
import { IAddress, ISearchableAddress } from "@bca-shared/models/address";
import { Form } from "@bca-shared/models/form";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import { TranslateService } from "@ngx-translate/core";
import { MonoTypeOperatorFunction, Subscription } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";

@Component({
  selector: "bca-user-address",
  templateUrl: "user-address.component.html",
  styleUrls: ["user-address.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class UserAddressComponent
  extends Form<IAddress>
  implements OnDestroy, OnInit
{
  @Input()
  addressForm: FormGroup;
  @Input()
  addressLoading: boolean;
  @Input()
  manualMode: boolean;
  @Input()
  searchFailed: boolean;
  @Input()
  searchableAddress: boolean;
  @Input()
  postcodeLength: number;
  @Input()
  isMobileView: boolean;

  @Output()
  search = new EventEmitter<ISearchableAddress>();
  @Output()
  manual = new EventEmitter<boolean>();

  subscriptions = new Subscription();

  private lastReportedZipCodeError: string | undefined;
  private lastReportedLastHouseNumberError: string | undefined;
  private lastReportedLastAddressError: string | undefined;
  private lastReportedStreetError: string | undefined;
  private lastReportedCityError: string | undefined;

  constructor(
    private _avoService: AvoService,
    private _translate: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.setFormGroup(this.addressForm);

    const { formGroup, search, subscriptions, filterInput } = this;

    subscriptions.add(
      formGroup
        .get("postcode")
        .valueChanges.pipe(
          filter(() => this.searchableAddress),
          filterInput.call(this),
          debounceTime(700),
        )
        .subscribe(() => {
          const postcode = formGroup.get("postcode").value;
          const houseNumber = formGroup.get("houseNumber").value;
          return (
            postcode &&
            houseNumber &&
            search.emit({ houseNumber, postcode } as ISearchableAddress)
          );
        }),
    );

    subscriptions.add(
      formGroup
        .get("houseNumber")
        .valueChanges.pipe(
          filter(() => this.searchableAddress),
          filterInput.call(this),
          debounceTime(500),
        )
        .subscribe(() => {
          const postcode = formGroup.get("postcode").value;
          const houseNumber = formGroup.get("houseNumber").value;
          return (
            postcode &&
            houseNumber &&
            search.emit({ houseNumber, postcode } as ISearchableAddress)
          );
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  filterInput = (): MonoTypeOperatorFunction<void> =>
    filter(() => {
      const { formGroup } = this;
      const houseNumberStatus = formGroup.get("houseNumber")
        .status as FormStatus;
      const postcodeStatus = formGroup.get("postcode").status as FormStatus;
      return (
        houseNumberStatus === FormStatus.VALID &&
        postcodeStatus === FormStatus.VALID
      );
    });

  getPostcodeErrorKey(): string | undefined {
    const formTagId = "postCode";
    let errorMessage;
    if (
      this.isTouchedAndDirty("postcode") &&
      this.hasError("postcode", "required")
    ) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.POSTCODE.ERROR_REQUIRED",
      );
    } else if (
      (this.isTouchedAndDirty("postcode") &&
        this.hasError("postcode", "minlength")) ||
      this.hasError("postcode", "maxlength")
    ) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.POSTCODE.ERROR_INVALID",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedZipCodeError) {
      this._avoService.formError({
        formName: "user_data_name",
        formId1: formTagId,
        errorMessage: [errorMessage],
      });
      this.lastReportedZipCodeError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedZipCodeError = undefined;
    }

    return errorMessage;
  }

  getHouseNumberErrorKey(): string | undefined {
    const formTagId = "houseNumber";
    let errorMessage: string | undefined;
    if (
      this.isTouchedAndDirty("houseNumber") &&
      this.hasError("houseNumber", "required")
    ) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.HOUSENUMBER.ERROR_REQUIRED",
      );
    } else {
      errorMessage = undefined;
    }

    if (
      errorMessage &&
      errorMessage !== this.lastReportedLastHouseNumberError
    ) {
      this._avoService.formError({
        formName: "user_data_name",
        formId1: formTagId,
        errorMessage: [errorMessage],
      });
      this.lastReportedLastHouseNumberError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedLastHouseNumberError = undefined;
    }

    return errorMessage;
  }

  getAddressErrorKey(): string | undefined {
    const formTagId = "addressNotFoundViaApi";
    let errorMessage: string | undefined;
    if (
      !(
        this.hasError("postcode", "minlength") ||
        this.hasError("postcode", "maxlength")
      )
    ) {
      if (
        this.searchFailed &&
        !this.manualMode &&
        this.searchableAddress &&
        !this.hasError("postcode", "required") &&
        !this.hasError("houseNumber", "required")
      ) {
        errorMessage = this._translate.instant(
          "USER_DATA.INPUT.ADDRESS.ERROR_INSTRUCTIONS",
        );
      }
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedLastAddressError) {
      this._avoService.formError({
        formName: "user_data_name",
        formId1: formTagId,
        errorMessage: ["API not recognized"],
      });
      this.lastReportedLastAddressError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedLastAddressError = undefined;
    }

    return errorMessage;
  }

  getStreetErrorKey(): string | undefined {
    const formTagId = "street";
    let errorMessage: string | undefined;
    if (
      this.isTouchedAndDirty("street") &&
      this.hasError("street", "required")
    ) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.STREET.ERROR_REQUIRED",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedStreetError) {
      this._avoService.formError({
        formName: "user_data_name",
        formId1: formTagId,
        errorMessage: [errorMessage],
      });
      this.lastReportedStreetError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedStreetError = undefined;
    }

    return errorMessage;
  }

  getCityErrorKey(): string | undefined {
    const formTagId = "street";
    let errorMessage: string | undefined;
    if (this.isTouchedAndDirty("city") && this.hasError("city", "required")) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.CITY.ERROR_REQUIRED",
      );
      return "USER_DATA.INPUT.CITY.ERROR_REQUIRED";
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedCityError) {
      this._avoService.formError({
        formName: "user_data_name",
        formId1: formTagId,
        errorMessage: [errorMessage],
      });
      this.lastReportedCityError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedCityError = undefined;
    }

    return errorMessage;
  }
}
